/**
 * Vanilla zustand store the audio engine writes into at ~30 Hz (never per audio quantum).
 * React must not subscribe per frame; the visual layer reads it inside its own rAF loop.
 */
import { createStore } from 'zustand/vanilla';
import type { EngineState } from '@/engine/types';

export interface EngineVoiceMeter {
  id: number;
  droneId: string;
  hz: number;
  /** 0..1 RMS-derived level of the voice */
  amplitude: number;
  latched: boolean;
  releasing: boolean;
  continuous: boolean;
  /** source unit kinds, e.g. ['sample','sample'] or ['fallback'] while the drone was still loading */
  kinds: string[];
}

export type LoadStatus = 'loading' | 'ready' | 'error';

export interface EngineStoreState {
  state: EngineState;
  voices: EngineVoiceMeter[];
  loadedDrones: string[];
  loading: Record<string, LoadStatus>;
  /** message per drone id when loading ended in 'error' (the drone still plays with fallback synths) */
  loadErrors: Record<string, string>;
  /** true while multisample roots are still rendering in the background (the drone already plays) */
  rendering: Record<string, boolean>;
  tonicHz: number;
  tanpuraOn: boolean;
  sampleRate: number;
  /** master output peak (absolute sample value, 0..1 after the limiter), updated ~30 Hz */
  masterPeak: number;
  /** how many times the watchdog rebuilt the graph after non-finite samples (should stay 0) */
  recoveries: number;
  pitchMode: string;
  /** per-drone load progress for the UI; removed ~1.5 s after the drone is fully ready */
  progress: Record<string, LoadProgress>;
}

export interface LoadProgress {
  /** what is happening right now */
  phase: 'fetching' | 'decoding' | 'rendering' | 'ready' | 'error';
  /** human label, e.g. "decoding 3 of 7 notes" */
  label: string;
  /** 0..1 */
  fraction: number;
  updatedAt: number;
}

export const engineStore = createStore<EngineStoreState>(() => ({
  state: 'idle',
  voices: [],
  loadedDrones: [],
  loading: {},
  loadErrors: {},
  rendering: {},
  tonicHz: 0,
  tanpuraOn: false,
  sampleRate: 0,
  masterPeak: 0,
  recoveries: 0,
  pitchMode: 'multisample',
  progress: {},
}));

export type EngineStore = typeof engineStore;
